import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Skeleton, Tabs, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { memberState } from "../../../../state/atoms";
import DataKitApi from "../../../../api/DataKitApi";
import {
  DataKitDto,
  DataKitGroupDto,
  DataKitItemDto,
  DataKitPublishDiffDto,
  MenuSummaryDto,
} from "../../../../domain/DataKitDto";
import { env } from "../../../../env/env";
import { DataKitItemsTab } from "./DataKitItemsTab";
import { DataKitItemDrawer, blankDataKitItem } from "./DataKitItemDrawer";
import { DataKitPublishBar } from "./DataKitPublishBar";
import { DataKitGroupsTab } from "./DataKitGroupsTab";
import { DataKitMenusTab } from "./DataKitMenusTab";
import { hasDataKitBeta } from "./dataKitAccess";
import "./DataKitDetailPage.scss";

export function DataKitDetailPage() {
  const { kitId } = useParams();
  const navigate = useNavigate();
  const member = useRecoilValue(memberState);
  const canWrite = !!member && (member.owner || member.role === "Admin");

  const [kit, setKit] = useState<DataKitDto | null>(null);
  const [items, setItems] = useState<DataKitItemDto[]>([]);
  const [groups, setGroups] = useState<DataKitGroupDto[]>([]);
  // The menus (designs) using this library's items - its Menus tab, and the drawer's menu price
  // picker's "other menus using this library" (Oct 12: menus come from designs).
  const [menus, setMenus] = useState<MenuSummaryDto[]>([]);
  const [diff, setDiff] = useState<DataKitPublishDiffDto | null>(null);
  /**
   * True until the first open has both the kit *and* its items.
   *
   * It used to be one `loading` flag dropped by `loading && !kit`, which meant the skeleton
   * vanished the moment the single-row kit read returned - fast - while the item list was still in
   * flight. The page then rendered with `items` empty, so the Items tab sat visibly blank, and the
   * header and tab counted 0 before jumping. Gated on both now, and only for the first open:
   * `reloadAll` runs after every mutation and must not throw the whole page back to a skeleton.
   */
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  /** Refreshes after the first load show the table's own spinner instead of an empty list. */
  const [itemsLoading, setItemsLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [editingItem, setEditingItem] = useState<DataKitItemDto | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedItemId = searchParams.get("item");

  const loadKit = useCallback(() => {
    if (!kitId) return Promise.resolve();
    return DataKitApi.getKit(kitId)
      .then((res) => setKit(res.data))
      .catch(() => message.error("Could not load this item library"));
  }, [kitId]);

  const loadItems = useCallback(
    (includeArchived: boolean) => {
      setItemsLoading(true);
      if (!kitId) return Promise.resolve();
      return DataKitApi.listItems(kitId, includeArchived)
        .then((res) => setItems(res.data || []))
        .catch(() => message.error("Could not load items"))
        .finally(() => setItemsLoading(false));
    },
    [kitId]
  );

  const loadGroups = useCallback(() => {
    if (!kitId) return Promise.resolve();
    return DataKitApi.listGroups(kitId)
      .then((res) => setGroups(res.data || []))
      .catch(() => undefined);
  }, [kitId]);

  const loadMenus = useCallback(() => {
    if (!kitId) return Promise.resolve();
    return DataKitApi.listKitMenus(kitId)
      .then((res) => setMenus(res.data || []))
      .catch(() => undefined);
  }, [kitId]);

  const loadDiff = useCallback(() => {
    if (!kitId) return Promise.resolve();
    return DataKitApi.getPublishDiff(kitId)
      .then((res) => setDiff(res.data))
      .catch(() => undefined);
  }, [kitId]);

  useEffect(() => {
    if (!kitId) return;
    setFirstLoadDone(false);
    // Only the two the page cannot render without. Groups, menus and the publish diff fill in
    // behind it - each feeds a tab or a badge that is fine arriving a moment later, and waiting on
    // the slowest of five is what made the first open feel broken.
    Promise.all([loadKit(), loadItems(true)]).finally(() => setFirstLoadDone(true));
    loadGroups();
    loadMenus();
    loadDiff();
  }, [kitId, loadKit, loadItems, loadGroups, loadMenus, loadDiff]);

  const reloadAll = useCallback(() => {
    loadKit();
    loadItems(true);
    loadGroups();
    loadMenus();
    loadDiff();
  }, [loadKit, loadItems, loadGroups, loadMenus, loadDiff]);

  /** Review changes' Edit: the item's drawer, from the list the page already holds. */
  const editItemById = useCallback(
    (itemId: string) => {
      const found = items.find((i) => i.id === itemId);
      if (found) {
        setEditingItem(found);
        return;
      }
      DataKitApi.getItem(itemId)
        .then((res) => setEditingItem(res.data))
        .catch(() => message.error("Could not open this item"));
    },
    [items]
  );

  // `?item=<id>` opens that item's drawer - how a result in the cross-kit search on the Data Kits
  // page lands on the item rather than just its kit. Dropped from the URL once used, so closing the
  // drawer and reloading doesn't reopen it.
  useEffect(() => {
    if (!linkedItemId || !firstLoadDone) return;
    const linked = items.find((i) => i.id === linkedItemId);
    if (linked) setEditingItem(linked);
    setSearchParams({}, { replace: true });
  }, [linkedItemId, firstLoadDone, items, setSearchParams]);

  const activeItems = useMemo(() => items.filter((i) => !i.archived), [items]);
  const archivedItems = useMemo(() => items.filter((i) => i.archived), [items]);

  if (!hasDataKitBeta(member)) return null;

  if (!firstLoadDone) {
    return (
      <div className="data-kit-detail-page">
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (!kit) return null;

  const itemCount = activeItems.length;
  const locationLabel = `${kit.locationCount} location${kit.locationCount === 1 ? "" : "s"}`;

  return (
    <div className="data-kit-detail-page">
      <nav className="data-kit-breadcrumb">
        <Link to={`${env.dashboardPath}/account/data-kits`}>Item Libraries</Link>
        <span className="data-kit-breadcrumb-sep">›</span>
        <span className="data-kit-breadcrumb-current">{kit.name || "Untitled"}</span>
      </nav>

      {diff && (
        <DataKitPublishBar
          kit={kit}
          diff={diff}
          canWrite={canWrite}
          onDone={reloadAll}
          onEditItem={editItemById}
        />
      )}

      <header className="data-kit-detail-header">
        <div className="data-kit-detail-header-text">
          <h1>{kit.name || "Untitled"}</h1>
          <p className="data-kit-detail-meta">
            {itemCount} item{itemCount === 1 ? "" : "s"} • {locationLabel}
          </p>
        </div>
        {canWrite && (
          <div className="data-kit-detail-actions">
            <Button
              className="data-kit-import-btn"
              onClick={() => navigate(`${env.dashboardPath}/account/data-kits/${kitId}/import`)}
            >
              Import
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="data-kit-add-item-btn"
              onClick={() => setEditingItem(blankDataKitItem())}
            >
              Add Item
            </Button>
          </div>
        )}
      </header>

      <Tabs
        className="data-kit-detail-tabs"
        items={[
          {
            key: "items",
            label: `Items • ${itemCount}`,
            children: (
              <DataKitItemsTab
                kit={kit}
                items={showArchived ? archivedItems : activeItems}
                groups={groups}
                archivedCount={archivedItems.length}
                showArchived={showArchived}
                onToggleArchived={() => setShowArchived((v) => !v)}
                canWrite={canWrite}
                loading={itemsLoading}
                onChanged={reloadAll}
                onEditItem={setEditingItem}
              />
            ),
          },
          {
            key: "groups",
            // Top-level groups only (Oct 12, QA): subgroups sit under their group as its sub-headers,
            // and counting them too made the number read as groups plus sub-headers.
            label: `Groups • ${groups.filter((g) => (g.path?.length ?? 1) <= 1).length}`,
            children: (
              <DataKitGroupsTab
                kit={kit}
                groups={groups}
                      canWrite={canWrite}
                onChanged={reloadAll}
              />
            ),
          },
          {
            key: "menus",
            label: `Menus • ${menus.length}`,
            children: <DataKitMenusTab menus={menus} />,
          },
        ]}
      />

      {editingItem && (
        <DataKitItemDrawer
          kit={kit}
          item={editingItem}
          groups={groups}
          menus={menus}
          canWrite={canWrite}
          onStructureChanged={loadGroups}
          onAdded={reloadAll}
          onClose={() => setEditingItem(null)}
          onSaved={() => {
            setEditingItem(null);
            reloadAll();
          }}
        />
      )}
    </div>
  );
}
